import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { useNotification } from '../context/NotificationContext';
import { Edit, Trash2, BookmarkX } from 'lucide-react';
import { CellEllipsis } from '../components/CellEllipsis';
import { countryCodes, parsePhoneNumber, formatPhoneNumber, getDefaultCountry, validatePhoneInput, getPhoneMaxLength, getPhonePlaceholder } from '../utils/countryCodes';
import { formatDateTime } from '../utils/dateUtils';
import './Profile.css';

// Human-readable page type labels for the saved filters table
const PAGE_TYPE_LABELS = {
  CHECK_DASHBOARD: 'Checks Main Page',
  CHECK_KPI_DASHBOARD: 'Checks Dashboard',
  CLARIFICATION_DASHBOARD: 'Clarification Checks'
};

// Build a concise summary string from a filter payload
const buildFilterSummary = (payload) => {
  if (!payload || typeof payload !== 'object') return '—';
  const parts = [];
  const addIf = (label, val) => {
    if (Array.isArray(val) && val.length) parts.push(`${label}: ${val.join(', ')}`);
    else if (val && typeof val === 'string' && val.trim()) parts.push(`${label}: ${val.trim()}`);
  };
  addIf('Status', payload.statuses);
  addIf('Excl. Status', payload.excludeStatuses);
  addIf('Practice', payload.practiceCodes);
  addIf('Location', payload.locationCodes);
  addIf('Check Type', payload.checkTypes);
  addIf('Workflow', payload.workflowStatuses);
  addIf('Assignee', payload.assigneeIds);
  addIf('Reporter', payload.reporterIds);
  addIf('Clarification Type', payload.clarificationType ? [payload.clarificationType] : null);
  addIf('Check #', payload.checkNumber);
  addIf('Payer', payload.payer);
  if (payload.depositDateFrom || payload.depositDateTo)   parts.push(`Deposit: ${payload.depositDateFrom || '?'} – ${payload.depositDateTo || '?'}`);
  if (payload.receivedDateFrom || payload.receivedDateTo)  parts.push(`Received: ${payload.receivedDateFrom || '?'} – ${payload.receivedDateTo || '?'}`);
  if (payload.completedDateFrom || payload.completedDateTo) parts.push(`Completed: ${payload.completedDateFrom || '?'} – ${payload.completedDateTo || '?'}`);
  if (payload.resolvedDateFrom || payload.resolvedDateTo)  parts.push(`Resolved: ${payload.resolvedDateFrom || '?'} – ${payload.resolvedDateTo || '?'}`);
  if (payload.openedDateFrom || payload.openedDateTo)     parts.push(`Opened: ${payload.openedDateFrom || '?'} – ${payload.openedDateTo || '?'}`);
  if (payload.startDate || payload.endDate)               parts.push(`Date: ${payload.startDate || '?'} – ${payload.endDate || '?'}`);
  return parts.length ? parts.join(' | ') : 'No filter values';
};

const Profile = () => {
  const { showError, showSuccess } = useNotification();
  const { user: authUser, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get('tab') || 'profile';
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneValue, setPhoneValue] = useState('');
  const [selectedCountryCode, setSelectedCountryCode] = useState(getDefaultCountry());
  const [updatingPhone, setUpdatingPhone] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  // Country code dropdown states
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState('');
  const countryRef = useRef(null);
  const [countryPos, setCountryPos] = useState(null);
  
  // Function to find all scrollable containers
  const findScrollableContainers = (element) => {
    const containers = [];
    let current = element;
    
    while (current && current !== document.body) {
      const style = window.getComputedStyle(current);
      const overflowY = style.overflowY || style.overflow;
      const overflowX = style.overflowX || style.overflow;
      
      if (overflowY === 'auto' || overflowY === 'scroll' || 
          overflowX === 'auto' || overflowX === 'scroll') {
        containers.push(current);
      }
      
      current = current.parentElement;
    }
    
    // Always include window
    containers.push(window);
    
    return containers;
  };

  // Function to calculate dropdown position
  const calculateDropdownPosition = (ref, setPosition) => {
    if (!ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    
    setPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280) // Ensure minimum width of 280px
    });
  };
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);

  // Saved Filters tab state
  const [savedFilters, setSavedFilters] = useState([]);
  const [savedFiltersLoading, setSavedFiltersLoading] = useState(false);
  const [sfTypeFilter, setSfTypeFilter] = useState('ALL');
  const [renamingFilter, setRenamingFilter] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [renameOrderValue, setRenameOrderValue] = useState('');
  const [isRenaming, setIsRenaming] = useState(false);
  const [deletingFilter, setDeletingFilter] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Close country code dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      const countryWrapper = event.target.closest('.country-code-select-wrapper');
      const countryDropdown = event.target.closest('.country-code-dropdown');
      if (!countryWrapper && !countryDropdown) {
        setCountryOpen(false);
        setCountryPos(null);
      }
    };
    
    const timeoutId = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside);
    }, 0);
    
    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Update dropdown positions on scroll/resize
  useEffect(() => {
    const updatePositions = () => {
      if (countryOpen && countryRef.current) {
        calculateDropdownPosition(countryRef, setCountryPos);
      }
    };
    
    // Find all scrollable containers for the open dropdown
    const scrollableContainers = new Set();
    
    if (countryOpen && countryRef.current) {
      findScrollableContainers(countryRef.current).forEach(container => scrollableContainers.add(container));
    }
    
    // Add scroll listeners to all scrollable containers
    const cleanupFunctions = [];
    scrollableContainers.forEach(container => {
      if (container === window) {
        container.addEventListener('scroll', updatePositions, true);
        container.addEventListener('resize', updatePositions);
        cleanupFunctions.push(() => {
          container.removeEventListener('scroll', updatePositions, true);
          container.removeEventListener('resize', updatePositions);
        });
      } else {
        container.addEventListener('scroll', updatePositions, true);
        cleanupFunctions.push(() => {
          container.removeEventListener('scroll', updatePositions, true);
        });
      }
    });
    
    // Also listen to window resize
    window.addEventListener('resize', updatePositions);
    cleanupFunctions.push(() => {
      window.removeEventListener('resize', updatePositions);
    });
    
    return () => {
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [countryOpen]);

  // Filter countries based on search term
  const filterCountries = (searchTerm) => {
    if (!searchTerm) return countryCodes;
    const lowerSearch = searchTerm.toLowerCase();
    return countryCodes.filter(country => 
      country.name.toLowerCase().includes(lowerSearch) ||
      country.dialCode.includes(lowerSearch) ||
      country.code.toLowerCase().includes(lowerSearch)
    );
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!authUser?.userId) {
        setError('User ID not found');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const userId = authUser.userId;
        const data = await api.getUserById(userId);
        setUserData(data);
        
        // Parse existing phone number to extract country code
        if (data.phone) {
          const parsed = parsePhoneNumber(data.phone);
          setSelectedCountryCode(parsed.countryCode || getDefaultCountry());
          setPhoneValue(parsed.phoneNumber);
        } else {
          setPhoneValue('');
          setSelectedCountryCode(getDefaultCountry());
        }
      } catch (err) {
        console.error('Error fetching user profile:', err);
        const errorMessage = err?.message || err?.data?.message || 'Failed to load profile. Please try again.';
        setError(errorMessage);
        showError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchUserProfile();
  }, [authUser, showError]);

  // Fetch all saved filters when Saved Filters tab is active
  useEffect(() => {
    if (activeTab === 'saved-filters') {
      fetchAllSavedFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchAllSavedFilters = async () => {
    setSavedFiltersLoading(true);
    try {
      const types = ['CHECK_DASHBOARD', 'CHECK_KPI_DASHBOARD', 'CLARIFICATION_DASHBOARD'];
      const results = await Promise.all(types.map(t => api.getSavedFilters(t).catch(() => [])));
      const all = results.flat().filter(Boolean);
      setSavedFilters(all);
    } catch {
      setSavedFilters([]);
    } finally {
      setSavedFiltersLoading(false);
    }
  };

  const handleRenameFilter = async () => {
    if (!renameValue.trim()) {
      showError('Please enter a filter name.');
      return;
    }
    const displayOrder = renameOrderValue !== '' ? parseInt(renameOrderValue, 10) : 0;
    setIsRenaming(true);
    try {
      await api.updateSavedFilter(renamingFilter.id, {
        name: renameValue.trim(),
        displayOrder: isNaN(displayOrder) ? 0 : displayOrder
      });
      showSuccess('Filter updated successfully.');
      setRenamingFilter(null);
      fetchAllSavedFilters();
    } catch (e) {
      showError(e?.message || e?.data?.message || 'Failed to update filter.');
    } finally {
      setIsRenaming(false);
    }
  };

  const handleDeleteFilter = async () => {
    setIsDeleting(true);
    try {
      await api.deleteSavedFilter(deletingFilter.id);
      showSuccess('Filter deleted successfully.');
      setDeletingFilter(null);
      fetchAllSavedFilters();
    } catch (e) {
      showError(e?.message || e?.data?.message || 'Failed to delete filter.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditPhone = () => {
    setEditingPhone(true);
    if (userData.phone) {
      const parsed = parsePhoneNumber(userData.phone);
      setSelectedCountryCode(parsed.countryCode || getDefaultCountry());
      setPhoneValue(parsed.phoneNumber);
    } else {
      setPhoneValue('');
      setSelectedCountryCode(getDefaultCountry());
    }
    setPhoneError('');
  };

  const handleCancelEditPhone = () => {
    setEditingPhone(false);
    if (userData.phone) {
      const parsed = parsePhoneNumber(userData.phone);
      setSelectedCountryCode(parsed.countryCode || getDefaultCountry());
      setPhoneValue(parsed.phoneNumber);
    } else {
      setPhoneValue('');
      setSelectedCountryCode(getDefaultCountry());
    }
    setPhoneError('');
    setCountryOpen(false);
    setCountryPos(null);
    setCountrySearch('');
  };

  const handleSavePhone = async () => {
    if (!phoneValue.trim()) {
      setPhoneError('Phone number is required');
      return;
    }

    try {
      setUpdatingPhone(true);
      setPhoneError('');
      const userId = authUser.userId;
      const fullPhoneNumber = formatPhoneNumber(selectedCountryCode, phoneValue.trim());
      await api.updateUser(userId, { phone: fullPhoneNumber });
      
      // Refresh user data
      const updatedData = await api.getUserById(userId);
      setUserData(updatedData);
      setEditingPhone(false);
      setCountryOpen(false);
      setCountryPos(null);
      setCountrySearch('');
    } catch (err) {
      console.error('Error updating phone:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to update phone number. Please try again.';
      setPhoneError(errorMessage);
      showError(errorMessage);
    } finally {
      setUpdatingPhone(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setPasswordError('');
    setPasswordSuccess(false);

    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setPasswordError('New password and confirm password do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setPasswordError('New password must be at least 8 characters long');
      return;
    }

    try {
      setChangingPassword(true);
      await api.changePassword(passwordData.currentPassword, passwordData.newPassword);
      setPasswordSuccess(true);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setTimeout(() => {
        setShowPasswordModal(false);
        setPasswordSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error changing password:', err);
      const errorMessage = err?.message || err?.data?.message || 'Failed to change password. Please check your current password.';
      setPasswordError(errorMessage);
      showError(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  };


  const getUserInitials = () => {
    if (userData) {
      const firstName = userData.firstName || '';
      const lastName = userData.lastName || '';
      if (firstName && lastName) {
        return `${firstName[0].toUpperCase()}${lastName[0].toUpperCase()}`;
      }
      if (userData.email) {
        return userData.email[0].toUpperCase();
      }
    }
    return 'U';
  };

  if (loading) {
    return (
      <div className="profile-page">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="profile-page">
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <h2>Error Loading Profile</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="profile-page">
        <div className="error-container">
          <p>No profile data available</p>
        </div>
      </div>
    );
  }

  // ── Saved Filters Tab ──────────────────────────────────────────────────────
  const renderSavedFilters = () => {
    const displayed = sfTypeFilter === 'ALL'
      ? savedFilters
      : savedFilters.filter(f => f.type === sfTypeFilter);

    return (
    <div className="saved-filters-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Saved Filters</h1>
          <p className="page-subtitle">Manage your saved filters across all pages</p>
        </div>
        {/* Type filter dropdown */}
        <div className="sf-type-filter-wrapper">
          <select
            className="sf-type-filter-select"
            value={sfTypeFilter}
            onChange={e => setSfTypeFilter(e.target.value)}
          >
            <option value="ALL">All Pages</option>
            <option value="CHECK_DASHBOARD">Checks Main Page</option>
            <option value="CHECK_KPI_DASHBOARD">Checks Dashboard</option>
            <option value="CLARIFICATION_DASHBOARD">Clarification Checks</option>
          </select>
        </div>
      </div>

      {savedFiltersLoading ? (
        <div className="loading-container">
          <div className="loading-spinner" />
          <p>Loading saved filters…</p>
        </div>
      ) : displayed.length === 0 ? (
        <div className="sf-empty-state">
          <BookmarkX size={48} strokeWidth={1.2} />
          <h3>{sfTypeFilter === 'ALL' ? 'No Saved Filters' : `No filters for ${PAGE_TYPE_LABELS[sfTypeFilter]}`}</h3>
          <p>Use the <strong>Save Filter</strong> button inside the Advanced Filters or Custom Filters panels on any page to save your frequently used filters here.</p>
        </div>
      ) : (
        <div className="sf-table-section">
          <table className="sf-table">
            <thead>
              <tr>
                <th className="sf-th-name">Filter Name</th>
                <th>Page</th>
                <th className="sf-th-summary">Filter Summary</th>
                <th>Sort Order</th>
                <th>Saved On</th>
                <th style={{ width: 96, textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {displayed.map(sf => (
                <tr key={sf.id}>
                  {/* Filter Name — max 20 chars display, full text on hover */}
                  <td className="sf-name-cell">
                    <CellEllipsis text={sf.name} />
                  </td>
                  <td>
                    <span className={`sf-page-badge sf-page-${sf.type}`}>
                      {PAGE_TYPE_LABELS[sf.type] || sf.type}
                    </span>
                  </td>
                  {/* Filter Summary — ellipsis + hover */}
                  <td className="sf-summary-cell">
                    <CellEllipsis text={buildFilterSummary(sf.payload)} />
                  </td>
                  {/* Sort Order — read-only in table, editable in popup */}
                  <td className="sf-order-cell">
                    {sf.displayOrder != null ? sf.displayOrder : '-'}
                  </td>
                  <td className="sf-date-cell">{formatDateTime(sf.createdAt) || '—'}</td>
                  <td className="sf-actions-cell">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}>
                        <Edit
                          size={18}
                          style={{ color: '#3b82f6', cursor: 'pointer', transition: 'color 0.2s' }}
                          onClick={() => {
                            setRenamingFilter(sf);
                            setRenameValue(sf.name);
                            setRenameOrderValue(sf.displayOrder != null ? String(sf.displayOrder) : '0');
                          }}
                          onMouseEnter={e => e.currentTarget.style.color = '#2563eb'}
                          onMouseLeave={e => e.currentTarget.style.color = '#3b82f6'}
                          title="Edit filter"
                        />
                      </div>
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => setDeletingFilter(sf)}
                        title="Delete filter"
                      >
                        <Trash2 size={18} style={{ color: '#ef4444' }} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Filter Modal */}
      {renamingFilter && (
        <div className="sf-modal-overlay" onClick={() => !isRenaming && setRenamingFilter(null)}>
          <div className="sf-modal" onClick={e => e.stopPropagation()}>
            <div className="sf-modal-header">
              <Edit size={17} />
              <h3>Edit Filter</h3>
              <button className="sf-modal-close" onClick={() => setRenamingFilter(null)} disabled={isRenaming}>
                <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="sf-modal-body">
              <label className="sf-modal-label">
                Filter Name <span className="save-filter-required">*</span>
              </label>
              <input
                type="text"
                className="sf-modal-input"
                value={renameValue}
                onChange={e => setRenameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setRenamingFilter(null); }}
                autoFocus
                maxLength={100}
                disabled={isRenaming}
              />
              <label className="sf-modal-label" style={{ marginTop: '12px' }}>
                Sort Order <span className="save-filter-hint-text">(optional)</span>
              </label>
              <input
                type="number"
                className="sf-modal-input"
                value={renameOrderValue}
                onChange={e => setRenameOrderValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Escape') setRenamingFilter(null); }}
                placeholder="e.g. 10"
                min="0"
                disabled={isRenaming}
              />
            </div>
            <div className="sf-modal-footer">
              <button className="sf-modal-btn-cancel" onClick={() => setRenamingFilter(null)} disabled={isRenaming}>Cancel</button>
              <button className="sf-modal-btn-confirm" onClick={handleRenameFilter} disabled={isRenaming || !renameValue.trim()}>
                {isRenaming ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm Modal */}
      {deletingFilter && (
        <div className="sf-modal-overlay" onClick={() => !isDeleting && setDeletingFilter(null)}>
          <div className="sf-modal" onClick={e => e.stopPropagation()}>
            <div className="sf-modal-header sf-modal-header-danger">
              <Trash2 size={17} />
              <h3>Delete Filter</h3>
              <button className="sf-modal-close" onClick={() => setDeletingFilter(null)} disabled={isDeleting}>
                <svg width="17" height="17" viewBox="0 0 20 20" fill="none">
                  <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </button>
            </div>
            <div className="sf-modal-body">
              <p className="sf-delete-confirm-text">
                Are you sure you want to delete <strong>"{deletingFilter.name}"</strong>? This cannot be undone.
              </p>
            </div>
            <div className="sf-modal-footer">
              <button className="sf-modal-btn-cancel" onClick={() => setDeletingFilter(null)} disabled={isDeleting}>Cancel</button>
              <button className="sf-modal-btn-danger" onClick={handleDeleteFilter} disabled={isDeleting}>
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );};
  // ───────────────────────────────────────────────────────────────────────────

  return (
    <div className="profile-page">
      {activeTab === 'saved-filters' && renderSavedFilters()}
      {activeTab === 'profile' && (<>
      <div className="page-header">
        <div>
          <h1 className="page-title">Profile</h1>
          <p className="page-subtitle">View and manage your account information</p>
        </div>
      </div>

      <div className="profile-content">
        <div className="profile-card">
          <div className="profile-header">
            <div className="profile-avatar-large">
              {userData.profileUrl ? (
                <img src={userData.profileUrl} alt="Profile" />
              ) : (
                <span>{getUserInitials()}</span>
              )}
            </div>
            <div className="profile-header-info">
              <div className="profile-info-content">
                <h2 className="profile-name">
                  {userData.firstName} {userData.lastName}
                </h2>
                <p className="profile-email">{userData.email}</p>
                <p className="profile-role">
                  {userData.roleMeta?.name ? 
                    userData.roleMeta.name.split('_').map(word => 
                      word.charAt(0) + word.slice(1).toLowerCase()
                    ).join(' ') 
                    : 'N/A'}
                </p>
                <div className="profile-status-badge">
                  <span className={`status-badge ${userData.status === 'ACTIVE' ? 'status-active' : 'status-inactive'}`}>
                    {userData.status}
                  </span>
                </div>
              </div>
              <div className="profile-header-actions">
                <button
                  className="btn-change-password-header"
                  onClick={() => setShowPasswordModal(true)}
                >
                  Change Password
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="profile-details-grid">
          <div className="detail-card">
            <h3 className="detail-card-title">Personal Information</h3>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">First Name</span>
                <span className="detail-value">{userData.firstName || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Last Name</span>
                <span className="detail-value">{userData.lastName || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Email</span>
                <span className="detail-value">{userData.email || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <div className="detail-item-header">
                  <span className="detail-label">Phone</span>
                  {!editingPhone && (
                    <button className="edit-btn" onClick={handleEditPhone} title="Edit phone number">
                      <Edit size={16} />
                    </button>
                  )}
                </div>
                {editingPhone ? (
                  <div className="edit-phone-container">
                    <div className="phone-input-group">
                      <div className="country-code-select-wrapper">
                        <div 
                          ref={countryRef}
                          className="country-code-select"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!updatingPhone) {
                              const isOpening = !countryOpen;
                              setCountryOpen(prev => !prev);
                              if (isOpening) {
                                setTimeout(() => calculateDropdownPosition(countryRef, setCountryPos), 0);
                              } else {
                                setCountryPos(null);
                              }
                            }
                          }}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                          }}
                          style={{ cursor: updatingPhone ? 'not-allowed' : 'pointer', position: 'relative', pointerEvents: 'auto' }}
                        >
                          <span className="country-code-display">
                            {selectedCountryCode.flag} {selectedCountryCode.dialCode}
                          </span>
                          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ marginLeft: '8px' }}>
                            <path d="M3 4.5L6 7.5L9 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      </div>
                      {countryOpen && !updatingPhone && countryPos && createPortal(
                        <div 
                          className="country-code-dropdown portal-dropdown" 
                          style={{
                            position: 'fixed',
                            top: `${countryPos.top}px`,
                            left: `${countryPos.left}px`,
                            width: `${countryPos.width}px`,
                            zIndex: 10050
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="text"
                            className="country-code-search"
                            placeholder="Search country..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            autoFocus
                          />
                          <div className="country-code-list">
                            {filterCountries(countrySearch).map((country) => (
                              <div
                                key={country.code}
                                className={`country-code-option ${selectedCountryCode.code === country.code ? 'selected' : ''}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedCountryCode(country);
                                  setCountrySearch('');
                                  setCountryOpen(false);
                                  setCountryPos(null);
                                  // Validate existing phone number when country changes
                                  if (phoneValue) {
                                    const validated = validatePhoneInput(phoneValue, country);
                                    setPhoneValue(validated);
                                  }
                                }}
                              >
                                <span className="country-code-display">
                                  {country.flag} {country.dialCode}
                                </span>
                                <span className="country-name">{country.name}</span>
                              </div>
                            ))}
                            {filterCountries(countrySearch).length === 0 && (
                              <div className="country-code-option no-results">No countries found</div>
                            )}
                          </div>
                        </div>,
                        document.body
                      )}
                      <input
                        type="tel"
                        value={phoneValue}
                        onChange={(e) => {
                          const validated = validatePhoneInput(e.target.value, selectedCountryCode);
                          setPhoneValue(validated);
                        }}
                        className="phone-input"
                        placeholder={getPhonePlaceholder(selectedCountryCode)}
                        maxLength={getPhoneMaxLength(selectedCountryCode)}
                        disabled={updatingPhone}
                      />
                    </div>
                    {phoneError && <div className="error-text">{phoneError}</div>}
                    <div className="edit-actions">
                      <button
                        className="btn-cancel"
                        onClick={handleCancelEditPhone}
                        disabled={updatingPhone}
                      >
                        Cancel
                      </button>
                      <button
                        className="btn-save"
                        onClick={handleSavePhone}
                        disabled={updatingPhone}
                      >
                        {updatingPhone ? 'Saving...' : 'Save'}
                      </button>
                    </div>
                  </div>
                ) : (
                  <span className="detail-value">{userData.phone || 'N/A'}</span>
                )}
              </div>
            </div>
          </div>

          <div className="detail-card">
            <h3 className="detail-card-title">Account History</h3>
            <div className="detail-list">
              <div className="detail-item">
                <span className="detail-label">Created At</span>
                <span className="detail-value">{formatDateTime(userData.createdAt) || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Created By</span>
                <span className="detail-value">
                  {userData.createdByMeta?.fullName || userData.createdByMeta?.email || 'N/A'}
                </span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Updated At</span>
                <span className="detail-value">{formatDateTime(userData.updatedAt) || 'N/A'}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">Updated By</span>
                <span className="detail-value">
                  {userData.updatedByMeta?.fullName || userData.updatedByMeta?.email || 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="logout-section">
          <button className="logout-button" onClick={async () => {
            await logout();
            navigate('/login');
          }}>
            Logout
          </button>
        </div>
      </div>
      </>)}

      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => !changingPassword && setShowPasswordModal(false)}>
          <div className="modal-content password-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button
                className="modal-close"
                onClick={() => setShowPasswordModal(false)}
                disabled={changingPassword}
              >
                ×
              </button>
            </div>
            <form onSubmit={handlePasswordChange} className="modal-form">
              <div className="form-group">
                <label htmlFor="currentPassword">Current Password</label>
                <input
                  type="password"
                  id="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  disabled={changingPassword}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="newPassword">New Password</label>
                <input
                  type="password"
                  id="newPassword"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  disabled={changingPassword}
                  required
                  minLength={8}
                />
                <small className="form-hint">Password must be at least 8 characters long</small>
              </div>
              <div className="form-group">
                <label htmlFor="confirmPassword">Confirm New Password</label>
                <input
                  type="password"
                  id="confirmPassword"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  disabled={changingPassword}
                  required
                  minLength={8}
                />
              </div>
              {passwordError && <div className="error-message">{passwordError}</div>}
              {passwordSuccess && (
                <div className="success-message">Password changed successfully!</div>
              )}
              <div className="modal-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={changingPassword}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={changingPassword}
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;

